import React, { useEffect, useRef, useState, useCallback } from 'react';
import './chat-container.css';
import { Button } from '../../components/button';
import { Input } from '../../components/input';
import { ChatMessage } from '../../components/chat';
import { ThreeDots } from 'react-loader-spinner';
import { useChat } from './hooks';

interface Message {
  position: 'left' | 'right';
  type: 'text' | 'image';
  title: string;
  text: string;
  userType: 'customer' | 'agent';
}

interface ChatContainerProps {
  country: number;
  affiliate: number;
}

const ChatContainer = ({country, affiliate}: ChatContainerProps) => {
  const [text, setText] = useState<string>('');
  const chat = useChat({ countryId: country, affiliateId: affiliate });
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const addNewMessage = useCallback(() => {
    let newText = text.replaceAll('\n', '').trim();
    if(!newText || chat.isLoadingMessage) return;

    const newMessage: Message = {
      position: 'right',
      type: 'text',
      title: 'Customer',
      text: newText,
      userType: 'customer',
    };
    chat.setMessages([...chat.messages, newMessage]);
    setText('');
    chat.setIsLoadingMessage(true);
  }, [chat.messages, chat.setMessages, chat.setIsLoadingMessage, text]);

  const handleSendMessage = (event: React.MouseEvent<HTMLButtonElement>) => {
    addNewMessage();
  };

  const handleResetChat = () => {
    chat.reset();
  };

  const handlePressEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      addNewMessage();
    }
  };

  const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    if(event.target.value == '\n') return;
    setText(event.target.value);
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chat.messages]);

  return (
    <div className={'container'}>
      <div className={'chat-container'} ref={chatContainerRef}>
        {chat.messages.map((message, index: number) => {
          return (
            <div className={'chat-message-item'} key={index}>
              <ChatMessage
                key={index}
                position={message.position}
                type={message.type}
                title={message.title}
                text={message.text}
              />
            </div>
          );
        })}
        {chat.isLoadingMessage && (
          <ThreeDots
            height="80"
            width="80"
            radius="9"
            color="#4fa94d"
            ariaLabel="three-dots-loading"
            wrapperStyle={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            visible={chat.isLoadingMessage}
          />
        )}
      </div>
      <div className={'bottom-container'}>
        <div className={'input-container'}>
          <Input
            variant="outlined"
            placeholder="Write here..."
            multiline={true}
            style={{ width: '100%' }}
            value={text}
            onChange={handleChangeText}
            onKeyDown={handlePressEnter}
            disabled={!chat.isActive}
          />
        </div>
        <div className={'button-container'}>
          <Button
            label="send"
            variant="contained"
            onClick={handleSendMessage}
            style={{ width: '100%', height: '100%' }}
            disabled={chat.isLoadingMessage || !chat.isActive}
          />
        </div>
        <div className={'button-container'}>
          <Button
            label="newChat"
            variant="contained"
            onClick={handleResetChat}
            style={{ width: '100%', height: '100%' }}
            disabled={chat.messages.length === 0}
            color="secondary"
          />
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;